import { useEffect, useMemo, useRef, useState } from "react";
import useTranslate from "../../hooks/useTranslate";
import { resolveCountry, Flag } from "./lawyerFlag";

// Chips input for multi-value free-text fields (specializations, languages) and
// suggestion-backed fields (jurisdictions -> country suggestions). Type + Enter
// (or comma) adds a token; backspace on an empty input removes the last one.
// `suggestions` (optional) shows a filtered dropdown of known values.
//
// Rebuild V2 presentation (artboards 1i/1j — logic unchanged):
//   - Empty + not editing -> dashed "+ Add …" affordance (1i) via `addLabel`;
//     clicking it opens the tag-input and focuses the text input.
//   - Tag-input container + removable tags per 1j (tag = #EBF3FF/#1567D2 pill,
//     remove glyph is the U+00D7 multiply char per SPEC-SYMBOLS #16, kept as a
//     real button so click-to-remove + aria-label survive).
//   - `withFlags` renders the platform image flag inside each tag (jurisdiction
//     field, 1j "Italy ×" tags — FLAG-EXCEPTION: never emoji).
const TokenInput = ({
  value = [],
  onChange,
  placeholder = "",
  suggestions = [],
  id,
  addLabel,
  withFlags = false,
}) => {
  const { t } = useTranslate();
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const tokens = Array.isArray(value) ? value : [];

  // The dashed 1i affordance replaces the input while empty; when it is
  // clicked we flip to editing and focus the (freshly mounted) input.
  const showAffordance = !!addLabel && tokens.length === 0 && !focused && !draft;

  useEffect(() => {
    if (focused && inputRef.current) inputRef.current.focus();
  }, [focused]);

  const addToken = (raw) => {
    const token = (raw || "").trim();
    if (!token) return;
    if (tokens.some((t) => t.toLowerCase() === token.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...tokens, token]);
    setDraft("");
  };

  const removeToken = (idx) => {
    onChange(tokens.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addToken(draft);
    } else if (e.key === "Backspace" && !draft && tokens.length) {
      removeToken(tokens.length - 1);
    }
  };

  const filteredSuggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return suggestions
      .filter(
        (s) =>
          !tokens.some((t) => t.toLowerCase() === s.toLowerCase()) &&
          (!q || s.toLowerCase().includes(q))
      )
      .slice(0, 6);
  }, [suggestions, draft, tokens]);

  if (showAffordance) {
    return (
      <button
        type="button"
        onClick={() => setFocused(true)}
        className="w-full text-left border-[1.5px] border-dashed border-[#C9D2E0] rounded-[9px] px-[12px] py-[10px] text-[13px] text-[#2283FF] font-semibold cursor-pointer bg-transparent"
      >
        {"+ "}
        {addLabel}
      </button>
    );
  }

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-[6px] px-[10px] py-[8px] border border-[#DDE3EC] rounded-[9px] bg-white focus-within:border-[#2283FF] focus-within:ring-2 focus-within:ring-[#2283FF]/20">
        {tokens.map((token, idx) => {
          const flag = withFlags ? resolveCountry(token) : null;
          return (
            <span
              key={`${token}-${idx}`}
              className="inline-flex items-center gap-[5px] bg-[#EBF3FF] text-[#1567D2] border border-[#C9E1FF] rounded-full px-[9px] py-[3px] text-[11.5px] font-semibold"
            >
              {flag?.code && (
                <Flag code={flag.code} width={16} height={11.5} label={token} />
              )}
              {token}
              <button
                type="button"
                onClick={() => removeToken(idx)}
                className="leading-none hover:opacity-70"
                aria-label={`${t("Remove")} ${token}`}
              >
                {"×"}
              </button>
            </span>
          );
        })}
        <input
          id={id}
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Delay so a suggestion click registers before blur closes it.
            setTimeout(() => setFocused(false), 120);
            addToken(draft);
          }}
          placeholder={tokens.length ? "" : placeholder}
          className="flex-1 min-w-[8rem] px-1 py-0.5 text-[13px] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none bg-transparent"
        />
      </div>

      {focused && filteredSuggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#E6EAF2] rounded-[10px] shadow-[0_8px_28px_rgba(16,24,40,0.12)] py-1 max-h-52 overflow-y-auto">
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addToken(s)}
              className="w-full text-left px-3 py-2 text-[13px] text-[#344054] hover:bg-[#F5F7FB]"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TokenInput;
